/**
 * WorkspacePanel - Middle panel (flex: 1)
 *
 * Scrollable card-based canvas architecture:
 *  ┌─────────────────────────────────┐
 *  │ Sticky title bar (metadata)     │
 *  ├─────────────────────────────────┤
 *  │                                 │ ← scrollable
 *  │  ┌─ SQL card (collapsed) ────┐  │
 *  │  │ SELECT w.state, SUM(...   │  │
 *  │  └──────────────────────────┘  │
 *  │                                 │
 *  │  ┌─ Data table card ─────────┐  │
 *  │  │  DataGrid (auto-height)   │  │
 *  │  └──────────────────────────┘  │
 *  │                                 │ ← future: chart card, insight card
 *  └─────────────────────────────────┘
 *
 * Scales to multiple SQL, table, and chart blocks.
 */

import React, { useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  useTheme,
  Divider,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import { AppDialog } from '../../../components/dialog';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import CodeRounded from '@mui/icons-material/CodeRounded';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import SpeedRounded from '@mui/icons-material/SpeedRounded';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRounded from '@mui/icons-material/UnfoldLessRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import GppGoodRounded from '@mui/icons-material/GppGoodRounded';
import DashboardCustomizeRounded from '@mui/icons-material/DashboardCustomizeRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import PushPinRounded from '@mui/icons-material/PushPinRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import ChatRounded from '@mui/icons-material/ChatRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import { formatRelativeTime } from '../../../utils/date';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/hljs/sql';
import { atomOneLight, atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { format as formatSQL } from 'sql-formatter';
import EChartsRenderer, { EChartsDownloadButton } from '../../../components/EChartsRenderer';
import OnboardingCoachmark from '../../../components/OnboardingCoachmark';
import { useBranding } from '../../../hooks/useBranding';
import { useProductOnboarding } from '../../../hooks/useProductOnboarding';
import type * as echarts from 'echarts/core';
import type { QueryResultTab, ChartArtifact, FindingArtifact, MetricArtifact, WorkspaceArtifact, SQLApproval } from '../stores/useCopilotStore';

SyntaxHighlighter.registerLanguage('sql', sql);

// ============================================================================
// CSV export helper
// ============================================================================

function exportToCsv(columns: string[], rows: any[][], title: string) {
  const escape = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(escape).join(',');
  const body = rows.map((row) => row.map(escape).join(',')).join('\n');
  const csv = header + '\n' + body;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/[^a-z0-9_\-]/gi, '_').toLowerCase() || 'export'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// Smart number formatter
// ============================================================================

function formatCellValue(val: number, colName: string): string {
  const lc = colName.toLowerCase();
  if (lc.includes('pct') || lc.includes('percent') || lc.includes('ratio') || lc.includes('%') || lc.includes('_of_total')) {
    if (Math.abs(val) <= 1.001 && !Number.isInteger(val)) return (val * 100).toFixed(2) + '%';
    return val.toFixed(2) + '%';
  }
  if (lc.includes('revenue') || lc.includes('amount') || lc.includes('price') || lc.includes('cost') || lc.includes('total') || lc.includes('salary') || lc.includes('budget')) {
    if (Math.abs(val) >= 1_000_000) return '$' + (val / 1_000_000).toFixed(2) + 'M';
    if (Math.abs(val) >= 1_000) return '$' + val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + val.toFixed(2);
  }
  if (Number.isInteger(val)) return val.toLocaleString();
  return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function isNumericColumn(rows: any[][], colIdx: number): boolean {
  let n = 0;
  const s = Math.min(rows.length, 20);
  for (let i = 0; i < s; i++) { if (typeof rows[i]?.[colIdx] === 'number') n++; }
  return n > s * 0.5;
}

// ============================================================================
// Main component
// ============================================================================

interface WorkspacePanelProps {
  artifact: WorkspaceArtifact | null;
  pendingSQLApproval?: SQLApproval | null;
  onSQLApprove?: () => void;
  onSQLEdit?: (editedSql: string) => void;
  onSQLReject?: (reason: string) => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** Title of the current thread (for canvas header context). */
  threadTitle?: string | null;
  /** Whether the right chat panel is collapsed (focus mode). */
  chatCollapsed?: boolean;
  /** Toggle the focus-mode collapse of the chat panel. */
  onToggleChat?: () => void;
  /** Streaming state — drives the empty/working placeholder. */
  isStreaming?: boolean;
  streamingAction?: string;
}

export default function WorkspacePanel({
  artifact,
  pendingSQLApproval,
  onSQLApprove,
  onSQLEdit,
  onSQLReject,
  onAddToDashboard,
  threadTitle,
  chatCollapsed,
  onToggleChat,
  isStreaming,
  streamingAction,
}: WorkspacePanelProps) {
  // SQL approval takes priority over regular artifacts
  if (pendingSQLApproval && onSQLApprove && onSQLEdit && onSQLReject) {
    return (
      <SQLApprovalCard
        approval={pendingSQLApproval}
        onApprove={onSQLApprove}
        onEdit={onSQLEdit}
        onReject={onSQLReject}
      />
    );
  }
  if (!artifact) return <WorkspaceEmptyState isStreaming={isStreaming} streamingAction={streamingAction} />;
  return (
    <ArtifactCanvas
      key={artifact.id}
      artifact={artifact}
      onAddToDashboard={onAddToDashboard}
      threadTitle={threadTitle}
      chatCollapsed={chatCollapsed}
      onToggleChat={onToggleChat}
    />
  );
}

// ============================================================================
// Artifact Canvas - scrollable card layout
// ============================================================================

export type ArtifactCanvasVariant = 'full' | 'inline';

export function ArtifactCanvas({
  artifact,
  onAddToDashboard,
  variant = 'full',
  showSql = true,
  embedTitleInCard,
  threadTitle,
  chatCollapsed,
  onToggleChat,
}: {
  artifact: WorkspaceArtifact;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** `inline` = bounded height for embedding in mobile unified chat */
  variant?: ArtifactCanvasVariant;
  /** When false, omit the SQL / data-source card (e.g. mobile Results artifact detail). */
  showSql?: boolean;
  /**
   * When true, artifact title + meta render inside the white card (not a separate bar above).
   * Defaults to true when `variant` is `inline`.
   */
  embedTitleInCard?: boolean;
  threadTitle?: string | null;
  chatCollapsed?: boolean;
  onToggleChat?: () => void;
}) {
  const theme = useTheme();
  const inline = variant === 'inline';
  const embedTitle = embedTitleInCard ?? inline;
  // Lifted from ChartCard so the canvas header can drive chart downloads
  // even though the ECharts instance is owned by the renderer below.
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(null);

  // Card styling sourced from design tokens — surface.raised on canvas,
  // hairline border, soft xs shadow.
  const cardSx = {
    border: `1px solid ${theme.tokens.border.subtle}`,
    borderRadius: `${theme.ds.radius.lg}px`,
    bgcolor: theme.tokens.surface.raised,
    boxShadow: theme.tokens.shadow.xs,
    overflow: 'hidden' as const,
  };

  // The card-level pin/download toolbar is only useful when the canvas
  // header is *not* being shown (i.e. embedded inline in mobile chat).
  // When the canvas header drives those actions, suppress them on cards.
  const showCardActions = embedTitle;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: inline ? 'auto' : '100%',
        maxHeight: inline ? 480 : undefined,
        minWidth: 0,
        animation: 'workspaceFadeIn 0.32s cubic-bezier(0.22,1,0.36,1)',
        '@keyframes workspaceFadeIn': {
          '0%': { opacity: 0, transform: 'translateY(8px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      {/* ── Canvas header: title + meta + unified action bar ───────────────── */}
      {!embedTitle && (
        <CanvasHeader
          artifact={artifact}
          threadTitle={threadTitle}
          chatCollapsed={chatCollapsed}
          onToggleChat={onToggleChat}
          onAddToDashboard={onAddToDashboard}
          chartInstance={chartInstance}
        />
      )}

      {/* ── Scrollable canvas ────────────────────────────────── */}
      <Box
        sx={(t) => ({
          flex: inline ? '0 1 auto' : 1,
          minHeight: inline ? 120 : 0,
          maxHeight: inline ? 400 : undefined,
          overflow: 'auto',
          p: embedTitle ? 0 : { xs: 1.5, sm: 2.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: embedTitle ? { xs: 1.25, sm: 1.5 } : { xs: 1.5, sm: 2 },
          bgcolor: embedTitle ? 'transparent' : t.tokens.surface.canvas,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: t.tokens.border.default,
            borderRadius: 3,
          },
        })}
      >
        {artifact.type === 'query_result' && (
          <QueryResultViewTabs
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            showSql={showSql}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
          />
        )}

        {artifact.type === 'chart' && (
          <ChartCard
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
            onChartInstanceChange={setChartInstance}
          />
        )}

        {artifact.type === 'metric_card' && (
          <MetricCardCard
            artifact={artifact}
            cardSx={cardSx}
            onAddToDashboard={showCardActions ? onAddToDashboard : undefined}
            embedTitle={embedTitle}
            showCardActions={showCardActions}
          />
        )}

        {/* findings are shown inline in chat, not in workspace */}
      </Box>
    </Box>
  );
}

// ============================================================================
// Canvas Header — Q. <question> + session meta + action bar
// ============================================================================

function CanvasHeader({
  artifact,
  threadTitle,
  chatCollapsed,
  onToggleChat,
  onAddToDashboard,
  chartInstance,
}: {
  artifact: WorkspaceArtifact;
  threadTitle?: string | null;
  chatCollapsed?: boolean;
  onToggleChat?: () => void;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  chartInstance?: echarts.ECharts | null;
}) {
  const [linkCopied, setLinkCopied] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState<HTMLElement | null>(null);

  // Onboarding coach-mark for the Pin to Dashboard button. Anchored to the
  // canvas-header pin button (the inline card button on desktop is hidden via
  // `showCardActions=false`, so the visual nudge has to live up here now).
  const { currentStepId, shouldShow: onboardingActive } = useProductOnboarding();
  const [dismissPinNudge, setDismissPinNudge] = useState(false);
  const pinBtnRef = useRef<HTMLButtonElement | null>(null);
  const showPinCoachmark =
    onboardingActive &&
    currentStepId === 'widget_added' &&
    !dismissPinNudge &&
    !!onAddToDashboard;

  // Headline = the AI-generated artifact title, e.g. "Leads Distribution by Source".
  // We deliberately do not show the originating user question here — users can
  // always read it in the chat panel, and the artifact title is the unique
  // identifier for what's on the canvas.
  const headline = artifact.title;

  // Meta line — small, dot-separated, derived from artifact + thread.
  const metaParts: Array<React.ReactNode> = [];
  if (threadTitle) metaParts.push(<span key="thread">{threadTitle}</span>);
  if (artifact.createdAt) {
    metaParts.push(
      <Box key="time" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
        <AccessTimeRounded sx={{ fontSize: 11 }} />
        {formatRelativeTime(artifact.createdAt)}
      </Box>
    );
  }
  if (artifact.type === 'query_result') {
    if (artifact.connectorName) {
      metaParts.push(
        <Box key="conn" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
          <StorageRounded sx={{ fontSize: 11 }} />
          {artifact.connectorName}
        </Box>
      );
    }
    metaParts.push(<span key="rows">{`${artifact.rowCount.toLocaleString()} rows`}</span>);
    if (typeof artifact.elapsedMs === 'number') {
      metaParts.push(<span key="elapsed">{`${artifact.elapsedMs}ms`}</span>);
    }
    if (artifact.truncated) {
      metaParts.push(
        <Box
          key="trunc"
          component="span"
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.375,
            color: t.tokens.semantic.warning,
            fontWeight: 600,
          })}
        >
          <WarningAmberRounded sx={{ fontSize: 11 }} />
          Truncated
        </Box>
      );
    }
  } else if (artifact.type === 'chart') {
    metaParts.push(<span key="type">{artifact.chartType}</span>);
    metaParts.push(<span key="rows">{`${artifact.rowCount.toLocaleString()} rows`}</span>);
  } else if (artifact.type === 'metric_card') {
    metaParts.push(<span key="metric">{artifact.metricName}</span>);
  }

  const handleCopyLink = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('artifact', artifact.id);
      const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // ── Download wiring (artifact-aware) ───────────────────────────────────
  const canDownloadTable = artifact.type === 'query_result';
  const canDownloadChart = artifact.type === 'chart' && !!chartInstance;
  const canDownload = canDownloadTable || canDownloadChart;

  const handleDownloadClick = (e: React.MouseEvent<HTMLElement>) => {
    if (canDownloadTable) {
      const a = artifact as QueryResultTab;
      exportToCsv(a.columns, a.rows, a.title);
      return;
    }
    if (canDownloadChart) {
      // Chart download has multiple formats (PNG/JPEG/SVG) → open menu.
      setDownloadAnchor(e.currentTarget);
    }
  };

  const handleChartDownload = (format: 'png' | 'jpeg' | 'svg') => {
    try {
      if (!chartInstance || chartInstance.isDisposed()) return;
      const dataUrl = chartInstance.getDataURL({
        type: format === 'svg' ? 'svg' : format,
        pixelRatio: 4,
        backgroundColor: '#fff',
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${(artifact.title || 'chart').replace(/[^a-z0-9_\-]/gi, '_').toLowerCase()}-${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setDownloadAnchor(null);
    }
  };

  const iconBtnSx = (t: any) => ({
    width: 30,
    height: 30,
    color: t.tokens.text.tertiary,
    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
  });

  return (
    <Box
      sx={(t) => ({
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.375,
        px: { xs: 2, sm: 2.5 },
        py: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      {/* Top row: artifact title + unified action bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            title={headline}
            sx={(t) => ({
              fontWeight: 600,
              fontSize: '0.9375rem',
              letterSpacing: '-0.005em',
              lineHeight: 1.4,
              color: t.tokens.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            })}
          >
            {headline}
          </Typography>
        </Box>

        {/* Unified action bar — all artifact-level CTAs live here */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          {canDownload && (
            <Tooltip
              title={canDownloadTable ? 'Download CSV' : 'Download chart'}
              placement="bottom"
            >
              <IconButton size="small" onClick={handleDownloadClick} sx={iconBtnSx}>
                <DownloadRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={linkCopied ? 'Link copied' : 'Copy link'} placement="bottom">
            <IconButton size="small" onClick={handleCopyLink} sx={iconBtnSx}>
              {linkCopied
                ? <CheckRounded sx={(t) => ({ fontSize: 16, color: t.tokens.semantic.success })} />
                : <LinkRounded sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          {onToggleChat && (
            <Tooltip title={chatCollapsed ? 'Show chat (\u2318\\)' : 'Focus mode \u2014 hide chat (\u2318\\)'} placement="bottom">
              <IconButton size="small" onClick={onToggleChat} sx={iconBtnSx}>
                {chatCollapsed ? <ChatRounded sx={{ fontSize: 16 }} /> : <ChatBubbleOutlineRounded sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          )}

          {/* Pin to dashboard — labeled because it's the primary save action */}
          {onAddToDashboard && (
            <>
              <Divider
                orientation="vertical"
                flexItem
                sx={(t) => ({ mx: 0.5, my: 0.5, borderColor: t.tokens.border.subtle })}
              />
              <Tooltip title="Pin to dashboard" placement="bottom">
                <Button
                  ref={pinBtnRef}
                  size="small"
                  variant="outlined"
                  startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                  onClick={() => {
                    // Dismiss the coach-mark immediately so the spotlight halo
                    // doesn't linger over the now-clicked button while the
                    // dashboard-picker modal animates in.
                    setDismissPinNudge(true);
                    onAddToDashboard(artifact);
                  }}
                  sx={(t) => ({
                    py: 0.375,
                    px: 1,
                    height: 30,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: `${t.ds.radius.sm}px`,
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    borderColor: showPinCoachmark ? t.tokens.brand.primary : t.tokens.border.default,
                    color: showPinCoachmark ? t.tokens.brand.primary : t.tokens.text.secondary,
                    bgcolor: showPinCoachmark ? t.tokens.brand.primarySoft : 'transparent',
                    '&:hover': {
                      borderColor: t.tokens.brand.primary,
                      color: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySoft,
                    },
                    animation: showPinCoachmark ? 'pinPulseHeader 1.6s ease-in-out infinite' : 'none',
                    '@keyframes pinPulseHeader': {
                      '0%':   { transform: 'scale(1)' },
                      '50%':  { transform: 'scale(1.03)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  })}
                >
                  Pin to dashboard
                </Button>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <OnboardingCoachmark
        open={showPinCoachmark}
        anchorRef={pinBtnRef}
        placement="bottom-end"
        message="Pin this chart to your dashboard."
      />

      {/* Meta line (session · time · source · rows) */}
      {metaParts.length > 0 && (
        <Box
          sx={(t) => ({
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            rowGap: 0.25,
            fontSize: '0.75rem',
            color: t.tokens.text.tertiary,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 0,
          })}
        >
          {metaParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Box component="span" sx={{ opacity: 0.5 }}>·</Box>}
              {part}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Chart download format menu */}
      {canDownloadChart && (
        <ChartDownloadMenu
          anchorEl={downloadAnchor}
          onClose={() => setDownloadAnchor(null)}
          onPick={handleChartDownload}
        />
      )}
    </Box>
  );
}

// Lightweight format picker so we don't bring in the heavier
// EChartsDownloadButton (which has its own anchor & icon).
function ChartDownloadMenu({
  anchorEl,
  onClose,
  onPick,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onPick: (format: 'png' | 'jpeg' | 'svg') => void;
}) {
  const open = Boolean(anchorEl);
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{ sx: { mt: 0.5, minWidth: 160 } }}
    >
      <MenuItem onClick={() => onPick('png')}>
        <ListItemText primary="Download as PNG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
      <MenuItem onClick={() => onPick('jpeg')}>
        <ListItemText primary="Download as JPEG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
      <MenuItem onClick={() => onPick('svg')}>
        <ListItemText primary="Download as SVG" primaryTypographyProps={{ fontSize: '0.8125rem' }} />
      </MenuItem>
    </Menu>
  );
}

// ============================================================================
// Query Result view tabs — switch between Table view and SQL/Source view
// ============================================================================

function QueryResultViewTabs({
  artifact,
  cardSx,
  onAddToDashboard,
  showSql,
  embedTitle,
  showCardActions = false,
}: {
  artifact: QueryResultTab;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  showSql: boolean;
  embedTitle: boolean;
  showCardActions?: boolean;
}) {
  type ViewMode = 'table' | 'sql';
  const [view, setView] = useState<ViewMode>('table');

  const isFile = artifact.isFileConnector || artifact.connectorCategory === 'files';
  // API/REST connectors (HubSpot, Shopify, Salesforce, Gong, …) issue JSON request
  // specs and the backend tags them as `connector_category === 'api'` (or 'rest'
  // historically). The fallback allow-list catches older threads where the field
  // wasn't sent.
  //
  // IMPORTANT: connectors that replicate to the warehouse (Airtable today;
  // HubSpot/Shopify next) are SQL queries against Redshift — never list them
  // here. Source of truth is `_API_CONNECTOR_TYPES` in
  // backend/agents/copilot/tools/sql_tools.py. Keep the two in lockstep.
  const apiConnectorTypes = new Set([
    'hubspot', 'shopify', 'salesforce', 'gong', 'stripe', 'notion', 'zendesk',
  ]);
  const isApi = !isFile && (
    artifact.connectorCategory === 'api'
    || artifact.connectorCategory === 'rest'
    || apiConnectorTypes.has(artifact.connectorType)
  );
  const sqlTabLabel = isFile ? 'Source' : isApi ? 'Request' : 'SQL';

  const tabBtnSx = (active: boolean) => (t: any) => ({
    position: 'relative',
    px: 1.25,
    py: 0.625,
    fontSize: '0.75rem',
    fontWeight: active ? 700 : 600,
    letterSpacing: '0.005em',
    color: active ? t.tokens.text.primary : t.tokens.text.tertiary,
    bgcolor: 'transparent',
    borderRadius: `${t.ds.radius.sm}px`,
    cursor: 'pointer',
    border: 0,
    fontFamily: 'inherit',
    transition: `color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}, background-color ${t.ds.motion.duration.fast}ms ${t.ds.motion.easing.standard}`,
    '&:hover': {
      color: t.tokens.text.primary,
      bgcolor: active ? 'transparent' : t.tokens.surface.sunken,
    },
    '&::after': active ? {
      content: '""',
      position: 'absolute',
      left: 8,
      right: 8,
      bottom: -1,
      height: 2,
      borderRadius: 1,
      bgcolor: t.tokens.brand.primary,
    } : {},
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.25, sm: 1.5 },
      }}
    >
      {/* Tab strip */}
      {showSql && (
        <Box
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            alignSelf: 'flex-start',
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
            px: 0.25,
          })}
        >
          <Box
            component="button"
            onClick={() => setView('table')}
            sx={tabBtnSx(view === 'table')}
            aria-pressed={view === 'table'}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <TableChartOutlined sx={{ fontSize: 13 }} />
              Table
              <Box component="span" sx={(t: any) => ({ fontWeight: 500, color: t.tokens.text.tertiary, fontSize: '0.6875rem' })}>
                {`(${artifact.rowCount.toLocaleString()})`}
              </Box>
            </Box>
          </Box>
          <Box
            component="button"
            onClick={() => setView('sql')}
            sx={tabBtnSx(view === 'sql')}
            aria-pressed={view === 'sql'}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <CodeRounded sx={{ fontSize: 13 }} />
              {sqlTabLabel}
            </Box>
          </Box>
        </Box>
      )}

      {/* Content */}
      {(!showSql || view === 'table') && (
        <DataTableCard
          artifact={artifact}
          cardSx={cardSx}
          onAddToDashboard={onAddToDashboard}
          embedTitle={embedTitle}
          showCardActions={showCardActions}
        />
      )}

      {showSql && view === 'sql' && (
        <SQLCard
          sql={artifact.sql}
          cardSx={cardSx}
          isFileConnector={isFile}
          isApiConnector={isApi}
          connectorName={artifact.connectorName}
          title={artifact.title}
          resultColumns={artifact.columns}
          defaultExpanded
        />
      )}
    </Box>
  );
}

/** Title + meta inside the card shell (unified mobile chat / compact artifact views). */
function ArtifactInlineTitleBlock({ artifact }: { artifact: WorkspaceArtifact }) {
  return (
    <Box
      sx={(t) => ({
        px: 1.75,
        pt: 1.25,
        pb: 1.25,
        borderBottom: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
      })}
    >
      <Typography
        sx={(t) => ({
          fontSize: '0.9375rem',
          fontWeight: 600,
          letterSpacing: '-0.005em',
          lineHeight: 1.4,
          mb: artifact.type === 'query_result' ? 0.875 : 0,
          color: t.tokens.text.primary,
          wordBreak: 'break-word',
        })}
      >
        {artifact.title}
      </Typography>
      {artifact.type === 'query_result' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
          <MetaBadge icon={<StorageRounded sx={{ fontSize: 12 }} />} label={artifact.connectorName} />
          <MetaBadge label={`${artifact.rowCount.toLocaleString()} rows`} />
          <MetaBadge label={`${artifact.elapsedMs}ms`} muted />
          {artifact.truncated && (
            <MetaBadge
              icon={<WarningAmberRounded sx={{ fontSize: 12 }} />}
              label="Truncated"
              color="warning"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// SQL Card - collapsed to 1-line preview by default
// ============================================================================

function SQLCard({
  sql: rawSql,
  cardSx,
  isFileConnector = false,
  isApiConnector = false,
  connectorName = '',
  title = '',
  resultColumns = [],
  defaultExpanded = false,
}: {
  sql: string;
  cardSx: Record<string, any>;
  isFileConnector?: boolean;
  isApiConnector?: boolean;
  connectorName?: string;
  title?: string;
  resultColumns?: string[];
  defaultExpanded?: boolean;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const codeBg = theme.tokens.surface.sunken;
  const lineNumberColor = theme.tokens.text.tertiary;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);
  const isNonSql = isFileConnector || isApiConnector;

  // Parse file connector JSON for clean display
  let fileEntityName = '';
  let fileColumns: string[] = [];
  if (isFileConnector) {
    try {
      const q = JSON.parse(rawSql);
      // Show human-friendly name: extract sheet/table name from entity_id (format: "fileId:SheetName")
      const eid = q.entity_id || '';
      const sheetName = eid.includes(':') ? eid.split(':').slice(1).join(':') : '';
      fileEntityName = sheetName || title || eid;
      // Use query-specified columns, fall back to actual result columns
      const queryCols = q.columns || [];
      fileColumns = queryCols.length > 0 ? queryCols : resultColumns;
    } catch { /* noop */ }
  }

  let apiResource = '';
  let apiProperties: string[] = [];
  if (isApiConnector) {
    try {
      const q = JSON.parse(rawSql);
      apiResource = q.resource || title || 'API resource';
      apiProperties = Array.isArray(q.properties) ? q.properties : [];
    } catch { /* noop */ }
  }

  let formattedSQL = rawSql;
  if (!isNonSql) {
    try { formattedSQL = formatSQL(rawSql, { language: 'postgresql' }); } catch { /* noop */ }
  }

  // 1-line preview
  const previewLine = isFileConnector
    ? (fileEntityName || 'File data source')
    : isApiConnector
      ? (apiResource || 'API request')
    : (formattedSQL.split('\n').find((l) => l.trim().length > 0) || rawSql.slice(0, 120));

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(rawSql);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ ...cardSx, ...(isNonSql ? { overflow: 'visible' } : {}) }}>
      {/* Header row - always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          px: 1.75,
          py: 1.125,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: t.tokens.sidebar.itemHoverBg },
        })}
      >
        <CodeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary, flexShrink: 0 })} />
        <Typography
          sx={(t) => ({
            ...t.typography.eyebrow,
            color: t.tokens.text.tertiary,
            flexShrink: 0,
          })}
        >
          {isFileConnector ? 'Data Source' : isApiConnector ? 'Query' : 'SQL'}
        </Typography>
        {isNonSql && connectorName && (
          <Typography
            component="span"
            sx={(t) => ({
              ml: 'auto',
              px: 1,
              py: 0.375,
              borderRadius: `${t.ds.radius.full}px`,
              bgcolor: t.tokens.surface.sunken,
              fontSize: '0.75rem',
              fontWeight: 500,
              color: t.tokens.text.secondary,
              flexShrink: 0,
            })}
          >
            {connectorName}
          </Typography>
        )}

        {/* 1-line preview when collapsed */}
        {!expanded && (
          <Typography
            noWrap
            title={previewLine}
            sx={(t) => ({
              flex: '1 1 0%',
              minWidth: 0,
              fontSize: '0.8125rem',
              fontFamily: isNonSql ? 'inherit' : t.typography.fontFamilyMono,
              color: t.tokens.text.secondary,
              ml: 0.5,
            })}
          >
            {previewLine}
          </Typography>
        )}
        {expanded && <Box sx={{ flex: 1 }} />}

        <Tooltip title={copied ? 'Copied!' : isApiConnector ? 'Copy query' : isFileConnector ? 'Copy request' : 'Copy SQL'} placement="top">
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={(t) => ({
              width: 28, height: 28,
              color: copied ? t.tokens.semantic.success : t.tokens.text.tertiary,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
            })}
          >
            {copied ? <CheckRounded sx={{ fontSize: 14 }} /> : <ContentCopyRounded sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title={expanded ? 'Collapse' : 'Expand'} placement="top">
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={(t) => ({
              width: 28, height: 28,
              color: t.tokens.text.tertiary,
              '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
            })}
          >
            {expanded
              ? <UnfoldLessRounded sx={{ fontSize: 16 }} />
              : <UnfoldMoreRounded sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded body */}
      {expanded && (
        <Box
          sx={(t) => ({
            px: 1,
            pb: 1,
            maxHeight: isNonSql ? 'none' : 280,
            overflow: 'auto',
            borderTop: `1px solid ${t.tokens.border.subtle}`,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: t.tokens.border.default,
              borderRadius: 2,
            },
          })}
        >
          {isFileConnector ? (
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography sx={(t) => ({ fontWeight: 600, fontSize: '0.875rem', color: t.tokens.text.primary })}>
                {fileEntityName || 'Unknown file'}
              </Typography>
              {fileColumns.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography
                    sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}
                  >
                    Columns ({fileColumns.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {fileColumns.map((col, i) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {col}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          ) : isApiConnector ? (
            <Box sx={{ px: 1.5, py: 1.5 }}>
              <Typography sx={(t) => ({ fontWeight: 600, fontSize: '0.875rem', color: t.tokens.text.primary })}>
                {apiResource || 'Unknown resource'}
              </Typography>
              {apiProperties.length > 0 && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography
                    sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}
                  >
                    Properties ({apiProperties.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {apiProperties.map((prop, i) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {prop}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
              <Box
                sx={(t) => ({
                  mt: 1.25,
                  p: 1.25,
                  borderRadius: `${t.ds.radius.sm}px`,
                  bgcolor: t.tokens.surface.sunken,
                  border: `1px solid ${t.tokens.border.subtle}`,
                  fontFamily: t.typography.fontFamilyMono,
                  fontSize: '0.75rem',
                  lineHeight: 1.65,
                  color: t.tokens.text.primary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                })}
              >
                {rawSql}
              </Box>
            </Box>
          ) : (
            <SyntaxHighlighter
              language="sql"
              style={isDark ? atomOneDark : atomOneLight}
              showLineNumbers
              lineNumberStyle={{
                minWidth: '2.4em',
                paddingRight: '1em',
                color: lineNumberColor,
                fontSize: '0.75rem',
                userSelect: 'none',
              }}
              customStyle={{
                margin: 0,
                padding: '12px',
                borderRadius: theme.ds.radius.sm,
                fontSize: '0.8125rem',
                lineHeight: 1.65,
                background: codeBg,
                fontFamily: theme.typography.fontFamilyMono as string,
              }}
            >
              {formattedSQL}
            </SyntaxHighlighter>
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Data Table Card - auto-height DataGrid in a card
// ============================================================================

function DataTableCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
}: {
  artifact: QueryResultTab;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  /** When true, artifact title + meta are the top section of this card */
  embedTitle?: boolean;
  /** When false, the canvas header drives pin/download — hide them on the card. */
  showCardActions?: boolean;
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const colMinW = isXs ? 88 : 120;

  const numericCols = useMemo(() => {
    const set = new Set<string>();
    artifact.columns.forEach((colName, idx) => {
      if (isNumericColumn(artifact.rows, idx)) set.add(colName);
    });
    return set;
  }, [artifact.columns, artifact.rows]);

  const gridColumns: GridColDef[] = useMemo(
    () =>
      artifact.columns.map((colName) => {
        const isNum = numericCols.has(colName);
        return {
          field: colName,
          headerName: colName,
          flex: 1,
          minWidth: colMinW,
          maxWidth: 400,
          sortable: true,
          headerAlign: isNum ? 'right' as const : 'left' as const,
          align: isNum ? 'right' as const : 'left' as const,
          renderCell: (params: any) => {
            const val = params.value;
            if (val === null || val === undefined) {
              return (
                <Typography sx={(t) => ({ color: t.tokens.text.disabled, fontStyle: 'italic', fontSize: '0.8125rem' })}>
                  —
                </Typography>
              );
            }
            if (typeof val === 'number') {
              return (
                <Typography
                  sx={(t) => ({
                    fontFamily: t.typography.fontFamilyMono,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: t.tokens.text.primary,
                    fontVariantNumeric: 'tabular-nums',
                  })}
                >
                  {formatCellValue(val, colName)}
                </Typography>
              );
            }
            return (
              <Typography
                sx={(t) => ({
                  fontSize: '0.8125rem',
                  color: t.tokens.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                })}
              >
                {String(val)}
              </Typography>
            );
          },
        };
      }),
    [artifact.columns, numericCols, theme, colMinW],
  );

  const gridRows = useMemo(
    () =>
      artifact.rows.map((row, idx) => {
        const obj: Record<string, any> = { id: idx };
        artifact.columns.forEach((col, ci) => { obj[col] = row[ci] ?? null; });
        return obj;
      }),
    [artifact.rows, artifact.columns],
  );

  // Auto-height: row height ~36px, header ~40px, footer ~44px, cap at 60vh (smaller cap on phones)
  const rowH = 36;
  const headerH = 40;
  const footerH = 44;
  const emptyOverlayH = 100; // breathing room when there are no rows
  const autoHeight = Math.min(
    artifact.rows.length === 0
      ? headerH + footerH + emptyOverlayH
      : headerH + footerH + artifact.rows.length * rowH + 2,
    window.innerHeight * (isXs ? 0.5 : 0.6),
  );

  return (
    <Box sx={cardSx}>
      {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
      {/* Card header — only shown when canvas header isn't taking over the actions
          (e.g. mobile inline chat). On the desktop canvas the header above
          carries Download/Pin/etc. so a second toolbar would be redundant. */}
      {showCardActions && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.875,
            rowGap: 0.5,
            px: 1.75,
            py: 1,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          <TableChartOutlined sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
          <Typography
            sx={(t) => ({
              ...t.typography.eyebrow,
              color: t.tokens.text.tertiary,
              flex: 1,
              minWidth: 48,
            })}
          >
            Results
          </Typography>
          <Tooltip title="Download CSV" placement="top">
            <IconButton
              size="small"
              onClick={() => exportToCsv(artifact.columns, artifact.rows, artifact.title)}
              sx={(t) => ({
                width: 28, height: 28,
                color: t.tokens.text.tertiary,
                '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
              })}
            >
              <DownloadRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {onAddToDashboard && (
            <Tooltip title="Pin to dashboard" placement="top">
              <Button
                size="small"
                variant="outlined"
                startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                onClick={() => onAddToDashboard(artifact)}
                sx={(t) => ({
                  fontSize: '0.75rem',
                  py: 0.375,
                  px: 1,
                  height: 28,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: `${t.ds.radius.sm}px`,
                  borderColor: t.tokens.border.default,
                  color: t.tokens.text.secondary,
                  minWidth: 0,
                  '&:hover': {
                    borderColor: t.tokens.brand.primary,
                    color: t.tokens.brand.primary,
                    bgcolor: t.tokens.brand.primarySoft,
                  },
                })}
              >
                Pin to dashboard
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {/* DataGrid */}
      <Box sx={{ height: autoHeight }}>
        <DataGrid
          rows={gridRows}
          columns={gridColumns}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          density="compact"
          disableRowSelectionOnClick
          sx={(t) => ({
            border: 0,
            borderRadius: 0,
            fontSize: '0.8125rem',
            color: t.tokens.text.primary,

            '& .MuiDataGrid-columnHeaders': {
              bgcolor: t.tokens.surface.sunken,
              borderBottom: `1px solid ${t.tokens.border.subtle}`,
              minHeight: '40px !important',
              maxHeight: '40px !important',
            },
            '& .MuiDataGrid-columnHeader': {
              '&:focus, &:focus-within': { outline: 'none' },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: t.tokens.text.tertiary,
            },
            '& .MuiDataGrid-columnSeparator': {
              color: t.tokens.border.default,
              '&:hover': { color: t.tokens.border.strong },
            },

            '& .MuiDataGrid-cell': {
              borderColor: t.tokens.border.subtle,
              '&:focus, &:focus-within': { outline: 'none' },
            },

            '& .MuiDataGrid-row': {
              '&:hover': {
                bgcolor: t.tokens.brand.primarySofter,
              },
            },

            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${t.tokens.border.subtle}`,
              minHeight: 44,
              bgcolor: t.tokens.surface.raised,
            },
            '& .MuiTablePagination-root': { fontSize: '0.75rem', color: t.tokens.text.secondary },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
            '& .MuiDataGrid-overlay': {
              bgcolor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8125rem',
              color: t.tokens.text.tertiary,
              letterSpacing: 0.2,
            },
          })}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// Finding Card - workspace artifact for pinned insights
// ============================================================================

function FindingCard({
  artifact,
  cardSx,
}: {
  artifact: FindingArtifact;
  cardSx: Record<string, any>;
}) {
  const dataPoints = artifact.dataPoint || {};
  const entries = Object.entries(dataPoints).slice(0, 8);

  return (
    <Box sx={cardSx}>
      {/* Card header */}
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.875,
          px: 1.75,
          py: 1,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.semantic.warningSoft,
        })}
      >
        <PushPinRounded sx={(t) => ({ fontSize: 14, color: t.tokens.semantic.warning, flexShrink: 0 })} />
        <Typography
          sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.semantic.warning })}
        >
          Finding
        </Typography>
        <Typography
          sx={(t) => ({
            ml: 0.5,
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: t.tokens.text.primary,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          {artifact.title}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.75 }}>
        {artifact.narrative && (
          <Typography
            sx={(t) => ({
              fontSize: '0.875rem',
              lineHeight: 1.65,
              color: t.tokens.text.primary,
              mb: entries.length > 0 ? 1.5 : 0,
            })}
          >
            {artifact.narrative}
          </Typography>
        )}

        {entries.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {entries.map(([k, v]) => (
              <Box
                key={k}
                sx={(t) => ({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.625,
                  px: 1,
                  py: 0.5,
                  borderRadius: `${t.ds.radius.full}px`,
                  bgcolor: t.tokens.surface.sunken,
                  border: `1px solid ${t.tokens.border.subtle}`,
                })}
              >
                <Typography
                  component="span"
                  sx={(t) => ({
                    fontSize: '0.75rem',
                    color: t.tokens.text.tertiary,
                    fontFamily: t.typography.fontFamilyMono,
                  })}
                >
                  {k}
                </Typography>
                <Typography
                  component="span"
                  sx={(t) => ({
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: t.tokens.text.primary,
                    fontFamily: t.typography.fontFamilyMono,
                    fontVariantNumeric: 'tabular-nums',
                  })}
                >
                  {typeof v === 'number' && Math.abs(v) < 1 && v !== 0
                    ? `${(v * 100).toFixed(1)}%`
                    : typeof v === 'number'
                    ? v.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(v)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Chart Card - renders ECharts spec inside a card shell
// ============================================================================

function ChartCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
  onChartInstanceChange,
}: {
  artifact: ChartArtifact;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  embedTitle?: boolean;
  /** When false, the canvas header drives pin/download — hide them on the card. */
  showCardActions?: boolean;
  /** Notify the parent canvas of the live ECharts instance, so the header can drive downloads. */
  onChartInstanceChange?: (instance: echarts.ECharts | null) => void;
}) {
  const { currentStepId, shouldShow } = useProductOnboarding();
  const showPinNudge = shouldShow && currentStepId === 'widget_added';
  const [dismissPinNudge, setDismissPinNudge] = useState(false);
  const pinBtnRef = useRef<HTMLButtonElement | null>(null);
  const pinBtnExpandedRef = useRef<HTMLButtonElement | null>(null);
  const showPinCoachmark = showPinNudge && !dismissPinNudge;
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { branding } = useBranding();
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const expandInstanceRef = useRef<echarts.ECharts | null>(null);
  const [expanded, setExpanded] = useState(false);

  const handleChartReady = useCallback((instance: echarts.ECharts) => {
    chartInstanceRef.current = instance;
    onChartInstanceChange?.(instance);
  }, [onChartInstanceChange]);

  const handleExpandChartReady = useCallback((instance: echarts.ECharts) => {
    expandInstanceRef.current = instance;
  }, []);

  // When this ChartCard is unmounted (e.g. switching artifacts) clear out the
  // canvas-header reference so it doesn't keep the old instance around.
  React.useEffect(() => {
    return () => {
      onChartInstanceChange?.(null);
    };
  }, [onChartInstanceChange]);

  // Adaptive height: scale with viewport so charts fill the workspace panel
  // rather than leaving dead zones on larger screens.
  const chartHeight = useMemo(() => {
    const spec = artifact.spec?._chartHeight ?? 420;
    if (isXs) {
      const cap = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.42) : 300;
      return Math.min(spec, Math.max(220, cap));
    }
    // Desktop: use the larger of the backend-computed spec height or 55% of viewport
    // so that charts fill the available workspace panel space on bigger screens.
    const viewportBased = typeof window !== 'undefined' ? Math.round(window.innerHeight * 0.55) : 420;
    return Math.min(Math.max(spec, viewportBased), 820);
  }, [artifact.spec?._chartHeight, isXs]);
  const getPinAnchorElement = useCallback(
    () => (expanded ? pinBtnExpandedRef.current : pinBtnRef.current),
    [expanded],
  );
  const iconBtnSx = (t: any) => ({
    width: 28,
    height: 28,
    color: t.tokens.text.tertiary,
    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
  });

  return (
    <>
      <Box sx={cardSx}>
        {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
        {/* Card header — only when the canvas header isn't already showing
            Download/Pin/Full-screen (i.e. embedded inline in mobile chat). */}
        {showCardActions && (
          <Box
            sx={(t) => ({
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.875,
              rowGap: 0.5,
              px: 1.75,
              py: 1,
              borderBottom: `1px solid ${t.tokens.border.subtle}`,
            })}
          >
            <BarChartRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
            <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
              Chart
            </Typography>
            <Box sx={{ flex: 1, minWidth: 40 }} />
            <Typography
              sx={(t) => ({
                fontSize: '0.75rem',
                color: t.tokens.text.tertiary,
                fontVariantNumeric: 'tabular-nums',
                mr: 0.5,
              })}
            >
              {artifact.rowCount.toLocaleString()} rows
            </Typography>

            <Divider orientation="vertical" flexItem sx={(t) => ({ mx: 0.25, borderColor: t.tokens.border.subtle })} />

            {/* Expand */}
            <Tooltip title="Expand" placement="top">
              <IconButton size="small" onClick={() => setExpanded(true)} sx={iconBtnSx}>
                <OpenInFullRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            {/* Download PNG/JPEG/SVG */}
            <EChartsDownloadButton chartInstance={chartInstanceRef.current} size="small" />

            {/* Add to dashboard - labeled button so it's clearly discoverable */}
            {onAddToDashboard && (
              <>
                <Divider orientation="vertical" flexItem sx={(t) => ({ mx: 0.5, borderColor: t.tokens.border.subtle })} />
                <Button
                  ref={pinBtnRef}
                  size="small"
                  variant="outlined"
                  startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                  onClick={() => { setDismissPinNudge(true); onAddToDashboard(artifact); }}
                  sx={(t) => ({
                    py: 0.375,
                    px: 1,
                    height: 28,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: `${t.ds.radius.sm}px`,
                    lineHeight: 1.5,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    borderColor: showPinCoachmark ? t.tokens.brand.primary : t.tokens.border.default,
                    color: showPinCoachmark ? t.tokens.brand.primary : t.tokens.text.secondary,
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: t.tokens.brand.primary,
                      color: t.tokens.brand.primary,
                      bgcolor: t.tokens.brand.primarySoft,
                    },
                    boxShadow: showPinCoachmark ? t.tokens.shadow.focusRing : 'none',
                    animation: showPinCoachmark && !isXs ? 'pinPulseChart 1.6s ease-in-out infinite' : 'none',
                    '@keyframes pinPulseChart': {
                      '0%':   { transform: 'scale(1)',   boxShadow: t.tokens.shadow.focusRing },
                      '70%':  { transform: 'scale(1.02)', boxShadow: '0 0 0 6px transparent' },
                      '100%': { transform: 'scale(1)',   boxShadow: '0 0 0 0 transparent' },
                    },
                  })}
                >
                  Pin to Dashboard
                </Button>
              </>
            )}
          </Box>
        )}
        {showCardActions && (
          <OnboardingCoachmark
            open={showPinCoachmark}
            getAnchorElement={getPinAnchorElement}
            placement="bottom-end"
            message={isXs ? 'Pin to your dashboard.' : 'Pin this chart to your dashboard.'}
          />
        )}

        {/* ECharts canvas */}
        <EChartsRenderer
          option={artifact.spec}
          branding={branding}
          height={chartHeight}
          onChartReady={handleChartReady}
        />
      </Box>

      <AppDialog
        open={expanded}
        onClose={() => setExpanded(false)}
        size="lg"
        icon={<BarChartRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />}
        title={artifact.title}
        headerExtras={
          <>
            <EChartsDownloadButton chartInstance={expandInstanceRef.current} size="small" />
            {onAddToDashboard && (
              <Button
                ref={pinBtnExpandedRef}
                size="small"
                variant="outlined"
                startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
                onClick={() => { setDismissPinNudge(true); onAddToDashboard(artifact); setExpanded(false); }}
                sx={(t) => ({
                  py: 0.375,
                  px: 1,
                  height: 30,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: `${t.ds.radius.sm}px`,
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  borderColor: t.tokens.border.default,
                  color: t.tokens.text.secondary,
                  '&:hover': {
                    borderColor: t.tokens.brand.primary,
                    color: t.tokens.brand.primary,
                    bgcolor: t.tokens.brand.primarySoft,
                  },
                })}
              >
                Pin to Dashboard
              </Button>
            )}
          </>
        }
        paperSx={{ width: '90vw', maxWidth: '90vw !important', height: '85vh' }}
        contentSx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
          <EChartsRenderer
            option={artifact.spec}
            branding={branding}
            fillContainer
            onChartReady={handleExpandChartReady}
          />
      </AppDialog>
    </>
  );
}

// ============================================================================
// Metric Card Card - renders a single KPI metric inside the workspace panel
// ============================================================================

function MetricCardCard({
  artifact,
  cardSx,
  onAddToDashboard,
  embedTitle = false,
  showCardActions = true,
}: {
  artifact: MetricArtifact;
  cardSx: Record<string, any>;
  onAddToDashboard?: (artifact: WorkspaceArtifact) => void;
  embedTitle?: boolean;
  /** When false, the canvas header drives pin — hide it on the card. */
  showCardActions?: boolean;
}) {
  const formattedValue = React.useMemo(() => {
    const v = artifact.metricValue;
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
      if (Math.abs(v) >= 1_000_000_000) return (v / 1_000_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'B';
      if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 }) + 'M';
      if (Math.abs(v) >= 1_000) return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
      return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
    return String(v);
  }, [artifact.metricValue]);

  return (
    <Box sx={cardSx}>
      {embedTitle && <ArtifactInlineTitleBlock artifact={artifact} />}
      {/* Card header — only when canvas header isn't carrying the pin action */}
      {showCardActions && (
        <Box
          sx={(t) => ({
            display: 'flex',
            alignItems: 'center',
            gap: 0.875,
            px: 1.75,
            py: 1,
            borderBottom: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          <SpeedRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
          <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
            Metric
          </Typography>
          <Box sx={{ flex: 1 }} />

          {onAddToDashboard && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DashboardCustomizeRounded sx={{ fontSize: 14 }} />}
              onClick={() => onAddToDashboard(artifact)}
              sx={(t) => ({
                py: 0.375,
                px: 1,
                height: 28,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: `${t.ds.radius.sm}px`,
                lineHeight: 1.5,
                minWidth: 0,
                whiteSpace: 'nowrap',
                borderColor: t.tokens.border.default,
                color: t.tokens.text.secondary,
                '&:hover': {
                  borderColor: t.tokens.brand.primary,
                  color: t.tokens.brand.primary,
                  bgcolor: t.tokens.brand.primarySoft,
                },
              })}
            >
              Pin to Dashboard
            </Button>
          )}
        </Box>
      )}

      {/* Metric display */}
      <Box
        sx={(t) => ({
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          bgcolor: t.tokens.surface.sunken,
          minHeight: 200,
        })}
      >
        <Typography
          sx={(t) => ({
            ...t.typography.eyebrow,
            color: t.tokens.text.tertiary,
            textAlign: 'center',
          })}
        >
          {artifact.metricName}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: t.tokens.text.primary,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1.05,
            textAlign: 'center',
          })}
        >
          {formattedValue}
        </Typography>
        {artifact.title && artifact.title !== artifact.metricName && (
          <Typography
            sx={(t) => ({
              fontSize: '0.8125rem',
              color: t.tokens.text.tertiary,
              textAlign: 'center',
              mt: 0.5,
            })}
          >
            {artifact.title}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Query Approval Card - inline workspace review for SQL / file / API requests
// ============================================================================

export type SQLApprovalCardVariant = 'full' | 'inline';

export function SQLApprovalCard({
  approval,
  onApprove,
  onEdit,
  onReject,
  variant = 'full',
}: {
  approval: SQLApproval;
  onApprove: () => void;
  onEdit: (editedSql: string) => void;
  onReject: (reason: string) => void;
  variant?: SQLApprovalCardVariant;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isEditing, setIsEditing] = useState(false);
  const [editedSql, setEditedSql] = useState(approval.sql);
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [copied, setCopied] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  const isFile = approval.isFileConnector || approval.connectorCategory === 'file';
  const isApi = approval.isApiConnector || approval.connectorCategory === 'api';
  const isNonSql = isFile || isApi;
  const inline = variant === 'inline';

  let formattedSQL = approval.sql;
  if (!isNonSql) {
    try { formattedSQL = formatSQL(approval.sql, { language: 'postgresql' }); } catch { /* noop */ }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(approval.sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const cardSx = {
    border: `1px solid ${theme.tokens.border.subtle}`,
    borderRadius: `${theme.ds.radius.lg}px`,
    bgcolor: theme.tokens.surface.raised,
    boxShadow: theme.tokens.shadow.xs,
    overflow: 'hidden' as const,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: inline ? 'auto' : '100%',
        maxHeight: inline ? 440 : undefined,
        minWidth: 0,
      }}
    >
      {/* Title bar */}
      <Box
        sx={(t) => ({
          height: 48,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          borderBottom: `1px solid ${t.tokens.border.subtle}`,
          bgcolor: t.tokens.semantic.warningSoft,
        })}
      >
        <GppGoodRounded sx={(t) => ({ fontSize: 18, color: t.tokens.semantic.warning })} />
        <Typography
          sx={(t) => ({
            fontWeight: 600,
            fontSize: '0.9375rem',
            letterSpacing: '-0.005em',
            color: t.tokens.text.primary,
            flex: 1,
          })}
        >
          {isFile ? 'Data access approval' : isApi ? 'API request approval' : 'SQL approval required'}
        </Typography>
        <MetaBadge icon={<StorageRounded sx={{ fontSize: 12 }} />} label={approval.connectorName} />
      </Box>

      {/* Scrollable content */}
      <Box
        sx={(t) => ({
          flex: inline ? '0 1 auto' : 1,
          maxHeight: inline ? 360 : undefined,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          bgcolor: t.tokens.surface.canvas,
        })}
      >
        {/* Description */}
        <Typography
          sx={(t) => ({
            fontSize: '0.875rem',
            color: t.tokens.text.secondary,
            lineHeight: 1.55,
          })}
        >
          {approval.title}
        </Typography>

        {isNonSql ? (
          /* ── File / API connectors: structured info ── */
          <Box sx={cardSx}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <Box>
                <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
                  {isFile ? 'Data source' : 'API resource'}
                </Typography>
                <Typography
                  sx={(t) => ({
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    letterSpacing: '-0.005em',
                    color: t.tokens.text.primary,
                    mt: 0.375,
                  })}
                >
                  {isFile
                    ? (approval.entityName || approval.entityId || 'Unknown file')
                    : (approval.resource || 'Unknown resource')}
                </Typography>
                <Typography sx={(t) => ({ fontSize: '0.8125rem', color: t.tokens.text.secondary, mt: 0.25 })}>
                  from {approval.connectorName}
                </Typography>
              </Box>

              {isFile && approval.columns && approval.columns.length > 0 && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                    Columns to fetch
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {approval.columns.map((col: string, i: number) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {col}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {isApi && approval.properties && approval.properties.length > 0 && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                    Properties
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {approval.properties.map((prop: string, i: number) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={(t) => ({
                          px: 0.875,
                          py: 0.25,
                          borderRadius: `${t.ds.radius.xs}px`,
                          bgcolor: t.tokens.surface.sunken,
                          fontFamily: t.typography.fontFamilyMono,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                        })}
                      >
                        {prop}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {isApi && approval.limit != null && (
                <Box>
                  <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary })}>
                    Limit
                  </Typography>
                  <Typography sx={(t) => ({ fontSize: '0.875rem', color: t.tokens.text.primary, mt: 0.375 })}>
                    {approval.limit}
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, display: 'block', mb: 0.625 })}>
                  {isFile ? 'Request spec' : 'API request'}
                </Typography>
                <Box
                  sx={(t) => ({
                    p: 1.25,
                    borderRadius: `${t.ds.radius.sm}px`,
                    bgcolor: t.tokens.surface.sunken,
                    border: `1px solid ${t.tokens.border.subtle}`,
                    fontFamily: t.typography.fontFamilyMono,
                    fontSize: '0.8125rem',
                    lineHeight: 1.65,
                    color: t.tokens.text.primary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  })}
                >
                  {approval.sql}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          /* ── SQL connector: code card ── */
          <Box sx={cardSx}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                px: 1.75,
                py: 1.125,
              }}
            >
              <CodeRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary, flexShrink: 0 })} />
              <Typography sx={(t) => ({ ...t.typography.eyebrow, color: t.tokens.text.tertiary, flex: 1 })}>
                Proposed SQL
              </Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy SQL'} placement="top">
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={(t) => ({
                    width: 28, height: 28,
                    color: copied ? t.tokens.semantic.success : t.tokens.text.tertiary,
                    '&:hover': { color: t.tokens.text.primary, bgcolor: t.tokens.surface.sunken },
                  })}
                >
                  {copied ? <CheckRounded sx={{ fontSize: 14 }} /> : <ContentCopyRounded sx={{ fontSize: 14 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* SQL body */}
            <Box
              sx={(t) => ({
                px: 1,
                pb: 1,
                maxHeight: 420,
                overflow: 'auto',
                borderTop: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              {isEditing ? (
                <TextField
                  multiline
                  fullWidth
                  minRows={8}
                  maxRows={20}
                  value={editedSql}
                  onChange={(e) => setEditedSql(e.target.value)}
                  sx={(t) => ({
                    mt: 1,
                    '& .MuiInputBase-input': {
                      fontFamily: t.typography.fontFamilyMono,
                      fontSize: '0.8125rem',
                      lineHeight: 1.65,
                    },
                  })}
                />
              ) : (
                <SyntaxHighlighter
                  language="sql"
                  style={isDark ? atomOneDark : atomOneLight}
                  showLineNumbers
                  lineNumberStyle={{
                    minWidth: '2.4em',
                    paddingRight: '1em',
                    color: theme.tokens.text.tertiary,
                    fontSize: '0.75rem',
                    userSelect: 'none',
                  }}
                  customStyle={{
                    margin: 0,
                    padding: '12px',
                    borderRadius: theme.ds.radius.sm,
                    fontSize: '0.8125rem',
                    lineHeight: 1.65,
                    background: theme.tokens.surface.sunken,
                    fontFamily: theme.typography.fontFamilyMono as string,
                  }}
                >
                  {formattedSQL}
                </SyntaxHighlighter>
              )}
            </Box>
          </Box>
        )}

        {/* Reject reason */}
        {showReject && (
          <TextField
            fullWidth
            label="Reason for rejection (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            size="small"
            sx={{ mt: 0.5 }}
          />
        )}

        {/* File feedback */}
        {showFeedback && (
          <TextField
            fullWidth
            label={isFile ? 'What should change? e.g. wrong file, need different columns…' : 'What should change? e.g. wrong resource, need different properties…'}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            size="small"
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 0.5 }}
            autoFocus
          />
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {showReject ? (
            <>
              <Button onClick={() => setShowReject(false)} size="small">
                Back
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlined />}
                onClick={() => onReject(rejectReason)}
                size="small"
              >
                Confirm Reject
              </Button>
            </>
          ) : (
            <>
              <Button
                color="error"
                startIcon={<CancelOutlined />}
                onClick={() => setShowReject(true)}
                size="small"
              >
                Reject
              </Button>
              <Box sx={{ flex: 1 }} />
              {isEditing ? (
                <>
                  <Button
                    onClick={() => { setIsEditing(false); setEditedSql(approval.sql); }}
                    size="small"
                  >
                    Cancel Edit
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircleOutlineRounded />}
                    onClick={() => onEdit(editedSql)}
                    disabled={!editedSql.trim()}
                    size="small"
                  >
                    Approve Edited SQL
                  </Button>
                </>
              ) : (
                <>
                  {isNonSql ? (
                    showFeedback ? (
                      <>
                        <Button
                          onClick={() => { setShowFeedback(false); setFeedback(''); }}
                          size="small"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditRounded />}
                          onClick={() => onReject(feedback)}
                          disabled={!feedback.trim()}
                          size="small"
                        >
                          Send Feedback
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<EditRounded />}
                          onClick={() => setShowFeedback(true)}
                          size="small"
                        >
                          Suggest Changes
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<CheckCircleOutlineRounded />}
                          onClick={onApprove}
                          size="small"
                        >
                          Approve & Run
                        </Button>
                      </>
                    )
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        startIcon={<EditRounded />}
                        onClick={() => setIsEditing(true)}
                        size="small"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CheckCircleOutlineRounded />}
                        onClick={onApprove}
                        size="small"
                      >
                        Approve & Run
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// Meta Badge - tiny label for title bar
// ============================================================================

function MetaBadge({
  icon,
  label,
  muted,
  color,
}: {
  icon?: React.ReactNode;
  label: string;
  muted?: boolean;
  color?: 'warning';
}) {
  const isWarning = color === 'warning';

  return (
    <Box
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.875,
        py: 0.375,
        borderRadius: `${t.ds.radius.full}px`,
        bgcolor: isWarning ? t.tokens.semantic.warningSoft : t.tokens.surface.sunken,
        color: isWarning
          ? t.tokens.semantic.warning
          : muted
            ? t.tokens.text.tertiary
            : t.tokens.text.secondary,
      })}
    >
      {icon}
      <Typography
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'inherit',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ============================================================================
// Empty State
// ============================================================================

// (Inline calculation-trail panel removed — full trail still available via the
// "View sources and calculation trail" link in chat.)

function WorkspaceEmptyState({
  isStreaming,
  streamingAction,
}: {
  isStreaming?: boolean;
  streamingAction?: string;
}) {
  if (isStreaming) {
    return <WorkspaceWorkingState action={streamingAction} />;
  }
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        bgcolor: t.tokens.surface.canvas,
        px: 3,
      })}
    >
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.brand.primarySoft,
          border: `1px solid ${t.tokens.border.subtle}`,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <SmartToyOutlined sx={{ fontSize: 26 }} />
      </Box>
      <Box sx={{ textAlign: 'center', maxWidth: 360 }}>
        <Typography
          sx={(t) => ({
            fontWeight: 600,
            mb: 0.5,
            fontSize: '0.9375rem',
            color: t.tokens.text.primary,
            letterSpacing: '-0.005em',
          })}
        >
          Your results land here
        </Typography>
        <Typography
          sx={(t) => ({
            color: t.tokens.text.secondary,
            lineHeight: 1.6,
            fontSize: '0.8125rem',
          })}
        >
          Tables, charts and metrics generated by Copilot show up here. Ask a question on the right to begin.
        </Typography>
      </Box>
      <Box
        sx={(t) => ({
          mt: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: t.tokens.text.tertiary,
          fontSize: '0.6875rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        })}
      >
        <KbdHint label="?" /> shortcuts
        <Box component="span" sx={{ opacity: 0.5 }}>·</Box>
        <KbdHint label="Cmd" /> <KbdHint label="\\" /> focus mode
      </Box>
    </Box>
  );
}

function KbdHint({ label }: { label: string }) {
  return (
    <Box
      component="span"
      sx={(t) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: 0.625,
        borderRadius: 0.75,
        bgcolor: t.tokens.surface.raised,
        border: `1px solid ${t.tokens.border.subtle}`,
        color: t.tokens.text.secondary,
        fontFamily: t.typography.fontFamilyMono,
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'none',
      })}
    >
      {label}
    </Box>
  );
}

function WorkspaceWorkingState({ action }: { action?: string }) {
  const label = action && action.trim().length > 0 ? action : 'Working on it...';
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2.5,
        height: '100%',
        bgcolor: t.tokens.surface.canvas,
        px: 3,
        textAlign: 'center',
      })}
    >
      <Box
        sx={(t) => ({
          position: 'relative',
          width: 56,
          height: 56,
          borderRadius: `${t.ds.radius.lg}px`,
          bgcolor: t.tokens.brand.primarySoft,
          border: `1px solid ${t.tokens.border.subtle}`,
          color: t.tokens.brand.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@keyframes wsPulseRing': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '70%': { transform: 'scale(1.35)', opacity: 0 },
            '100%': { transform: 'scale(1.35)', opacity: 0 },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            border: `1px solid ${t.tokens.brand.primary}`,
            animation: 'wsPulseRing 1.6s ease-out infinite',
          },
        })}
      >
        <SmartToyOutlined sx={{ fontSize: 26 }} />
      </Box>
      <Box sx={{ maxWidth: 360 }}>
        <Typography
          sx={(t) => ({
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: t.tokens.text.primary,
            letterSpacing: '-0.005em',
            mb: 0.5,
          })}
        >
          {label}
        </Typography>
        <Typography
          sx={(t) => ({
            fontSize: '0.8125rem',
            color: t.tokens.text.secondary,
            lineHeight: 1.6,
          })}
        >
          Copilot is gathering data and building results. Tables and charts will appear as they're ready.
        </Typography>
      </Box>
      {/* Skeleton placeholder */}
      <Box
        sx={(t) => ({
          width: '100%',
          maxWidth: 520,
          mt: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '@keyframes wsSkeletonShimmer': {
            '0%': { backgroundPosition: '-200% 0' },
            '100%': { backgroundPosition: '200% 0' },
          },
        })}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={(t) => ({
              height: i === 0 ? 26 : 14,
              borderRadius: 1,
              opacity: i === 0 ? 1 : 0.7,
              background: `linear-gradient(90deg, ${t.tokens.surface.sunken} 0%, ${t.tokens.border.subtle} 50%, ${t.tokens.surface.sunken} 100%)`,
              backgroundSize: '200% 100%',
              animation: 'wsSkeletonShimmer 1.6s linear infinite',
            })}
          />
        ))}
      </Box>
    </Box>
  );
}
